import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  useTheme
} from "@mui/material";
import * as React from "react";
import {ArrowDropDownIcon} from "@mui/x-date-pickers";
import Box from "@mui/material/Box";
import {useState} from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

/**
 *
 * @param variant
 * @param size
 * @param color
 * @param title
 * @param sx
 * @param startIcon
 * @param onClick
 * @param children
 * @param disabled
 * @param component
 * @param href
 * @param target
 * @return {Element}
 * @constructor
 */
export function ButtonBase({
                             variant,
                             size,
                             color,
                             title,
                             sx,
                             startIcon,
                             onClick,
                             children,
                             disabled,
                             component,
                             href,
                             target
                           }) {
  const theme = useTheme();

  return (
    <Button
      variant={variant || "contained"}
      startIcon={startIcon}
      size={size || "small"}
      component={component}
      href={href}
      target={target}
      sx={{
        ...theme.Button,
        color: theme.palette.text.default,
        textDecoration: "none",
        ...sx
      }}
      color={color}
      disabled={disabled}
      onClick={onClick ? onClick : null}>
      {children || title}
    </Button>);
}

/**
 * Кнопка скачивания
 * @param variant
 * @param size
 * @param color
 * @param href
 * @param title
 * @param sx
 * @return {Element}
 * @constructor
 */
export function ButtonDownload({variant, size, color, href, title, sx}) {
  const onDownload = () => {
    if (!href)
      return;
    const link = document.createElement("a");
    link.href = href;
    link.click();
  };

  return (
    <ButtonBase onClick={onDownload}>
      {title}
    </ButtonBase>);
}

/**
 * Кнопка скачивания
 * @param variant
 * @param size
 * @param color
 * @param href
 * @param title
 * @param sx
 * @return {Element}
 * @constructor
 */
export function ButtonLink({variant, size, color, title, sx, startIcon, onClick, children, disabled, href, target}) {
  return (
    <ButtonBase
      component="a"
      href={href}
      target="_blank"
      variant={variant}
      size={size}
      color={color}
      title={title}
      sx={sx}
      startIcon={startIcon}
      onClick={onClick}
      disabled={disabled}/>
  );
}

/**
 * Аплоад
 * @param onSubmit
 * @param title
 * @returns {Element}
 * @constructor
 */
export function ButtonUpload({onSubmit, title, sx}) {
  async function handleSubmit(event) {
    event.preventDefault()
    const file = event.target.files[0];

    if (!file)
      return;

    const formData = new FormData();
    formData.append(`file`, file);

    onSubmit({formData});

    // Сбрасываем значение, чтобы можно было выбрать тот же файл снова
    event.target.value = '';
  }

  const fileInputRef = React.useRef(null);

  // Обработчик клика по кнопке
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <ButtonBase sx={{...sx}} onClick={handleButtonClick}>
      {title || 'Загрузить'}
      <input type="file" ref={fileInputRef} onChange={handleSubmit} hidden/>
    </ButtonBase>
  )
}

/**
 *
 * @param onButtonClick
 * @param disabled
 * @param options
 * @return {Element}
 * @constructor
 */
export function ButtonGroupCustom({
                                    onButtonClick,
                                    onMenuClick,
                                    disabled,
                                    options,
                                    sxButton,
                                    sxArrowButton,
                                    sxMenuList,
                                    title,
                                    openOnButtonClick,
                                    customBtn
                                  }) {
  const [open, setOpen] = useState(false);

  const anchorRef = React.useRef(null);
  const theme = useTheme();

  // обработка выпадающего меню
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleMenuClick = (option, index) => {
    if (option.onClick) {
      option.onClick(option.key, index)
    } else if (onMenuClick) {
      onMenuClick(option.key, index)
    }

    return;
  }

  return (
    <ButtonGroup
      variant="contained"
      ref={anchorRef}
      size="small"
    >
      {customBtn ? customBtn : <ButtonBase
        onClick={() => openOnButtonClick ? handleToggle() : onButtonClick()}
        disabled={disabled}
        sx={{sxButton}}>{title || "Добавить"}
      </ButtonBase>}
      <Button
        size="small"
        aria-controls={open ? 'split-button-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="menu"
        onClick={handleToggle}
        disabled={disabled}
        sx={{minWidth: 80, color: theme.palette.text.default, ...sxArrowButton}}
      >
        <ArrowDropDownIcon style={{width: 20}}/>
        <Popper
          sx={{zIndex: 1}}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({TransitionProps, placement}) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem style={sxMenuList}>
                    {
                      options.map((option, index) => (
                        <MenuItem
                          key={option.key}
                          onClick={() => handleMenuClick(option, index)}
                          sx={{textTransform: 'none'}}
                        >
                          {option.value}
                        </MenuItem>
                      ))
                    }
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Button>
    </ButtonGroup>
  )
}


// Универсальный компонент кнопки "Назад"
export const BackButton = ({
                             href,
                             label = "список",
                             color,
                             iconSize = "12pt",
                             buttonSize = "medium",
                             ...props
                           }) => {
  const theme = useTheme();

  return (
    <a href={href} style={{textDecoration: 'none'}}>
      <Box sx={{lineHeight: '50px'}} display="flex" justifyContent="flex-start">
        <IconButton
          style={{backgroundColor: 'transparent'}}
          sx={{
            p: '8px',
            color: color || theme.palette.icon.default,
            fontSize: '12pt'
          }}
          {...props}
          aria-label="directions">
          <ArrowBackIosIcon sx={{fontSize: '12pt'}}/>{label}
        </IconButton>
      </Box>
    </a>
  );
};

export const BackButtonToClientsList = (props) => (
  <BackButton
    href="/clients"
    label="список"
    aria-label="Вернуться к списку клиентов"
  />
);

export const BackButtonToDealList = (props) => (
  <BackButton
    href="/"
    label="список"
    aria-label="Вернуться к списку сделок"
    {...props}
  />
);

export const BackButtonToDeal = (props) => (
  <BackButton
    href={props.dealId ? `/deal?id=${props.dealId}` : '/'}
    label="к сделке"
    aria-label="Вернуться к сделкe"
    {...props}
  />
);